import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardList, Ruler, LineChart, Clock, Receipt, PackageMinus, PackageCheck, HardHat, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const CARDS = [
  { key: "daily_progress", label: "Daily Progress Reports", icon: ClipboardList, to: (id) => `/projects/${id}/daily-reports` },
  { key: "site_measurements", label: "Site Measurements", icon: Ruler, to: (id) => `/projects/${id}/construction` },
  { key: "plan_vs_actual", label: "Plan vs Actual", icon: LineChart, to: (id) => `/projects/${id}/plan-vs-actual` },
  { key: "delays", label: "Open Delays", icon: Clock, to: (id) => `/projects/${id}/delays` },
  { key: "cost_entries", label: "Cost Entries", icon: Receipt, to: (id) => `/projects/${id}/cost-entries` },
  { key: "goods_issues", label: "Goods Issues", icon: PackageMinus, to: () => `/erp/operations/goods-issues` },
  { key: "store_returns", label: "Store Returns", icon: PackageCheck, to: () => `/erp/operations/store-returns` },
  { key: "contractor_jmrs", label: "Contractor JMRs", icon: HardHat, to: () => `/erp/operations/contractor-jmrs` },
];

export default function SiteHub() {
  const { projectId } = useProjectCtx();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/site-hub`);
      setHub(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="site-hub-page">
      <PageHeader title="Site Management Hub" description="One place to jump into every day-to-day site module for this project." />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4" data-testid="site-hub-cards">
          {CARDS.map((c) => {
            const data = hub?.[c.key] || { count: 0, latest_date: null };
            const Icon = c.icon;
            return (
              <button
                key={c.key}
                onClick={() => navigate(c.to(projectId))}
                className="group rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/50 hover:bg-accent/40"
                data-testid={`site-hub-card-${c.key}`}
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
                <p className="mt-3 font-mono text-2xl font-bold text-foreground">{data.count}</p>
                <p className="mt-0.5 text-xs font-semibold text-foreground">{c.label}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">Latest {fmtDate(data.latest_date)}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
